"use client";

import { type ComponentPropsWithRef, Suspense } from "react";

import { useTranslations } from "next-intl";

import { PrimRadioButtons } from "@/shared-web.ui/prim.radio-buttons";
import { Text } from "@/shared-web.ui/ui.text";
import { useVariantState } from "@/stf.m.product/feat.product-detail";

import {
  type ServerDataOutput,
  extractSubscriptionDuration,
  useSubscriptionSelectorActions,
} from "../data.subscription-data";
import * as SelectorContainer from "../prim.selector-container";

export type DurationCardProps = {
  duration: number;
};
export const DurationCard = (props: DurationCardProps) => {
  const { duration } = props;

  const t = useTranslations("stf_m_subscription.screen_subscription.duration_selector");
  return (
    <div className={`
      flex h-12 flex-row items-center justify-center gap-1
      align-middle
    `}
    >
      <div className="flex flex-row items-baseline gap-1">
        <Text size="4" weight="medium">
          {duration}
        </Text>
        <Text size="2" weight="regular" color="gray">
          {t("duration", {
            duration,
          })}
        </Text>
      </div>
    </div>
  );
};

interface DurationSelectorDisplayProps
  extends Pick<ComponentPropsWithRef<typeof PrimRadioButtons.Root>, "onValueChange" | "value"> {
  data: ServerDataOutput;
}
const DurationSelectorDisplay = (props: DurationSelectorDisplayProps) => {
  const { value, onValueChange, data } = props;
  const {
    subscriptionVariant: { subscriptionDurationVariant },
  } = data;
  const { variantOptionsArray, variantOptions } = subscriptionDurationVariant;

  const t = useTranslations("stf_m_subscription.screen_subscription.duration_selector");

  return (
    <SelectorContainer.Root>
      <SelectorContainer.Title>
        {t("set_your_subscription_length")}
      </SelectorContainer.Title>
      <SelectorContainer.Body>
        <PrimRadioButtons.Root
          className={`
            grid grid-cols-2
            md:grid-cols-4
          `}
          value={value}
          onValueChange={onValueChange}
        >
          {variantOptionsArray.map(({ optionKey }) => {
            const options = variantOptions[optionKey];
            const duration = extractSubscriptionDuration(options.optionName);
            return (
              <PrimRadioButtons.Item value={optionKey} key={optionKey}>
                <DurationCard duration={duration} />
              </PrimRadioButtons.Item>
            );
          })}
        </PrimRadioButtons.Root>
      </SelectorContainer.Body>
    </SelectorContainer.Root>
  );
};

interface DurationSelectorWithParamsProps {
  data: ServerDataOutput;
}
const DurationSelectorWithParams = (props: DurationSelectorWithParamsProps) => {
  const { data } = props;
  const { opts } = useVariantState();
  const { setVal } = useSubscriptionSelectorActions({ data });
  const {
    subscriptionData: {
      subscriptionMeta: { variantMapper },
    },
  } = data;

  return (
    <DurationSelectorDisplay
      data={data}
      value={opts?.[variantMapper.subscriptionDuration]}
      onValueChange={(v) => {
        setVal("subscriptionDuration", v);
      }}
    />
  );
};

interface DurationSelectorProps {
  data: ServerDataOutput;
}
export const DurationSelector = (props: DurationSelectorProps) => {
  const { data } = props;
  return (
    <Suspense fallback={<DurationSelectorDisplay data={data} />}>
      <DurationSelectorWithParams data={data} />
    </Suspense>
  );
};
